import { Link, useNavigate } from 'react-router-dom';
import { Badge, BrandLockup, Card, QueryState, Stat, Table, type Column } from '@integra/ui';
import { useAdminOverview, useLogout } from '../api/hooks.js';
import type { EstateActivity, EstateOrganisation } from '../api/types.js';

/**
 * Platform administrator surface (§6): the estate-wide overview across every
 * organisation. It lives OUTSIDE the workspace shell because it is cross-tenant,
 * and it is reached only when the single sign-in resolved a platformRole of
 * 'admin'. The API guards it again with requirePlatformAdmin, so this screen is
 * never the sole gate. Read-only: it observes the estate, it does not mutate it.
 */

const nf = new Intl.NumberFormat();

function humanOperation(op: string): string {
  return op.replace(/[._]/g, ' ');
}

function shortId(id: string | null): string {
  if (id === null) return 'system';
  return id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

export function AdminOverviewPage() {
  const navigate = useNavigate();
  const overview = useAdminOverview();
  const logout = useLogout();

  const orgColumns: readonly Column<EstateOrganisation>[] = [
    {
      key: 'name',
      header: 'Organisation',
      render: (o) => <span className="font-semibold text-navy-700">{o.name}</span>,
      sortValue: (o) => o.name.toLowerCase(),
    },
    {
      key: 'planTier',
      header: 'Plan',
      render: (o) => <Badge tone="gold">{o.planTier}</Badge>,
      sortValue: (o) => o.planTier,
    },
    {
      key: 'workspaces',
      header: 'Workspaces',
      align: 'right',
      numeric: true,
      render: (o) => nf.format(o.workspaces),
      sortValue: (o) => o.workspaces,
    },
    {
      key: 'users',
      header: 'Users',
      align: 'right',
      numeric: true,
      render: (o) => nf.format(o.users),
      sortValue: (o) => o.users,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (o) => new Date(o.createdAt).toLocaleDateString(),
      sortValue: (o) => new Date(o.createdAt).getTime(),
    },
  ];

  const activityColumns: readonly Column<EstateActivity>[] = [
    {
      key: 'operation',
      header: 'Action',
      render: (a) => <span className="text-navy-700">{humanOperation(a.operation)}</span>,
      sortValue: (a) => a.operation,
    },
    {
      key: 'actorId',
      header: 'Actor',
      render: (a) => <span className="font-mono text-xs">{shortId(a.actorId)}</span>,
    },
    {
      key: 'workspaceId',
      header: 'Workspace',
      render: (a) => (
        <span className="font-mono text-xs">
          {a.workspaceId === null ? 'org-level' : shortId(a.workspaceId)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'When',
      render: (a) => new Date(a.createdAt).toLocaleString(),
      sortValue: (a) => new Date(a.createdAt).getTime(),
    },
  ];

  return (
    <div className="reading-layer min-h-screen bg-surface-muted">
      {/* Estate chrome: navy, distinct from a workspace, with the mark as home. */}
      <header className="border-b border-navy-600 bg-navy-700 text-ink-inverse">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3.5 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              aria-label="Estate overview home"
              className="rounded-md transition-opacity duration-fast hover:opacity-80"
            >
              <BrandLockup tone="light" />
            </button>
            <span className="rounded-full border border-gold-400 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gold-400">
              Administrator
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/workspaces"
              className="text-sm text-navy-300 transition-colors duration-fast hover:text-ink-inverse"
            >
              Workspaces
            </Link>
            {/* Plain button, not the ghost Button primitive: ghost is `text-navy-700`,
                which is invisible on this navy header. Matches the sibling link's
                light-on-navy treatment instead. */}
            <button
              type="button"
              onClick={() => logout.mutate()}
              className="rounded-md text-sm text-navy-300 transition-colors duration-fast hover:text-ink-inverse"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl px-6 py-6">
        <div className="mt-5">
          <QueryState
            loading={overview.isLoading}
            error={overview.error}
            what="the estate overview"
          >
            {overview.data !== undefined && (
              <div className="flex flex-col gap-5">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                  {(
                    [
                      ['Organisations', overview.data.totals.organisations],
                      ['Workspaces', overview.data.totals.workspaces],
                      ['Users', overview.data.totals.users],
                      ['Campaigns', overview.data.totals.campaigns],
                      ['Channels', overview.data.totals.destinations],
                      ['Connectors', overview.data.totals.connectors],
                    ] as const
                  ).map(([label, value]) => (
                    <Card key={label}>
                      <Stat label={label} value={nf.format(value)} provenance="none" />
                    </Card>
                  ))}
                </div>

                <Card title="Organisations" subtitle="Every client on the platform.">
                  <Table
                    columns={orgColumns}
                    rows={overview.data.organisations}
                    rowKey={(o) => o.id}
                    empty="No organisations yet."
                  />
                </Card>

                <Card
                  title="Recent activity"
                  subtitle="The latest audited actions across the estate."
                >
                  <Table
                    columns={activityColumns}
                    rows={overview.data.recentActivity}
                    rowKey={(a) => `${a.createdAt}-${a.operation}-${a.actorId ?? 'sys'}`}
                    empty="No activity recorded yet."
                  />
                </Card>
              </div>
            )}
          </QueryState>
        </div>
      </main>
    </div>
  );
}
